import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  assertNever,
  cn,
  ControlMonitorEndpointSummary,
  getPartnerEventName,
  PartnerEvents,
  queries,
  useAuthConfig,
  ValidControlCategory,
} from "@theprelude/core";
import { Button, ServerIcon } from "@theprelude/ds";
import { Filter } from "@/lib/filter";
import { tb } from "@/lib/tracking";
import { useExceptedFilters } from "@/queries/control-monitor";
import { getNavigationOptions, Metric } from "../sections";
import { SummaryStatusIcon } from "../summary-icon-bubbles";
import { SingleMetricCard } from "./single-metric-card";

type MissingControlPartnerEventCode =
  | typeof PartnerEvents.NO_EDR
  | typeof PartnerEvents.NO_ASSET_MANAGER
  | typeof PartnerEvents.NO_VULN_MANAGER;

export const MissingControlCategoryCard = ({
  category,
  partnerEvent,
  description,
}: {
  category: ValidControlCategory;
  partnerEvent: MissingControlPartnerEventCode;
  description: string;
}) => {
  const filters = useMissingCategoryFilters(partnerEvent);
  const authConfig = useAuthConfig();
  const { showExcepted } = useExceptedFilters();

  const { data: summary } = useSuspenseQuery(queries.scm.summary(authConfig));

  const missingCount = getMissingCount({
    endpointSummary: summary.endpoint_summary,
    partnerEvent,
    showExcepted,
  });

  const title = getPartnerEventName(partnerEvent);

  return (
    <SingleMetricCard
      icon={<SummaryStatusIcon success={missingCount === 0} />}
      title={title}
      description={description}
      metric={<Metric label="Devices" count={missingCount} />}
    >
      <div className="flex items-center gap-4">
        <Link
          {...getNavigationOptions({
            overlay: [
              "control-category-failures",
              { category, partnerEvent, filters },
            ],
          })}
        >
          <Button
            intent="secondary"
            icon={<ServerIcon className="text-secondary-foreground" />}
            className={cn("inline-flex", tb(title))}
          >
            View devices
          </Button>
        </Link>
      </div>
    </SingleMetricCard>
  );
};

const useMissingCategoryFilters = (
  partnerEvent: MissingControlPartnerEventCode,
): Filter[] => [
  {
    id: getFilterField(partnerEvent),
    operator: "is",
    value: true,
  } satisfies Filter,
];

const getFilterField = (
  partnerEvent: MissingControlPartnerEventCode,
): string => {
  switch (partnerEvent) {
    case PartnerEvents.NO_ASSET_MANAGER: {
      return "missing_asset_manager";
    }
    case PartnerEvents.NO_EDR: {
      return "missing_edr";
    }
    case PartnerEvents.NO_VULN_MANAGER: {
      return "missing_vuln_manager";
    }
    default: {
      return assertNever(partnerEvent);
    }
  }
};

const getMissingCount = ({
  endpointSummary,
  partnerEvent,
  showExcepted,
}: {
  endpointSummary: ControlMonitorEndpointSummary;
  partnerEvent: MissingControlPartnerEventCode;
  showExcepted: boolean;
}) => {
  const property = getEndpointSummaryProperty(partnerEvent);
  const missingCount =
    (showExcepted
      ? endpointSummary[property]
      : endpointSummary.excepted[property]) ?? 0;
  return missingCount;
};

const getEndpointSummaryProperty = (
  partnerEvent: MissingControlPartnerEventCode,
): keyof ControlMonitorEndpointSummary &
  keyof ControlMonitorEndpointSummary["excepted"] => {
  switch (partnerEvent) {
    case PartnerEvents.NO_ASSET_MANAGER: {
      return "missing_asset_manager_count";
    }
    case PartnerEvents.NO_EDR: {
      return "missing_edr_count";
    }
    case PartnerEvents.NO_VULN_MANAGER: {
      return "missing_vuln_manager_count";
    }
    default: {
      return assertNever(partnerEvent);
    }
  }
};
